import React, { useState, useEffect } from "react";
import useApprovedCollectionsInfiniteQuery from "../../Collection/hooks/usePublicCollectionsInfiniteQuery";
import DSCheckBox from "../../system-design/DSCheckBox";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSButton from "../../system-design/DSButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTextField from "../../system-design/DSTextField";
import DSTypography from "../../system-design/DSTypography";
import DSIconButton from "../../system-design/DSIconButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { MachineCollection } from "../../../Types";
import { FaLock, FaStar } from "react-icons/fa";
import useDeviceType from "../../../components/hooks/useDeviceType";
import CollectionDialog from "../../Collection/components/CollectionDialog";

interface CollectionsPickDialogProps {
  onClose: () => void;
  selectedCollections: MachineCollection[];
  onPickCollections: (selectedCollections: MachineCollection[]) => void;
  limit?: number; // Optional limit for number of selections
}

function CollectionsPickDialog({
  onClose,
  selectedCollections,
  onPickCollections,
  limit,
}: CollectionsPickDialogProps) {
  const [selected, setSelected] =
    useState<MachineCollection[]>(selectedCollections);
  const [permanentCollections, setPermanentCollections] = useState<Set<string>>(
    new Set(selectedCollections.filter((c) => c.permanent).map((c) => c.id)),
  );
  const [themedCollections, setThemedCollections] = useState<Set<string>>(
    new Set(selectedCollections.filter((c) => c.themed).map((c) => c.id)),
  );
  const [searchText, setSearchText] = useState("");
  // A themed collection must carry its themed-menu assets. When an admin marks
  // one themed without them, we open its collection modal so they can add them
  // (helped by the modal's "Use cover" / "Use pendulum logo" shortcuts).
  const [assetModalCollectionId, setAssetModalCollectionId] = useState<
    string | null
  >(null);
  const { isSmallTablet } = useDeviceType();

  const {
    data: collections,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useApprovedCollectionsInfiniteQuery(searchText);

  useEffect(() => {
    setSelected(selectedCollections);
    setPermanentCollections(
      new Set(selectedCollections.filter((c) => c.permanent).map((c) => c.id)),
    );
    setThemedCollections(
      new Set(selectedCollections.filter((c) => c.themed).map((c) => c.id)),
    );
  }, [selectedCollections]);

  const handleToggleCollection = (collectionId: string) => {
    setSelected((prev) => {
      const exists = prev.find((c) => c.id === collectionId);
      if (exists) {
        // Deselect the collection
        return prev.filter((c) => c.id !== collectionId);
      } else {
        // Select the collection
        const newCollection: MachineCollection = {
          id: collectionId,
          permanent: permanentCollections.has(collectionId),
          themed: themedCollections.has(collectionId),
        };
        if (limit && prev.length >= limit) {
          // If limit is reached, replace the selection (for single selection)
          return limit === 1
            ? [newCollection]
            : [...prev.slice(1), newCollection];
        }
        return [...prev, newCollection];
      }
    });
  };

  const handleTogglePermanent = (collectionId: string) => {
    setPermanentCollections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(collectionId)) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });

    // Update the selected array with the new permanent status
    setSelected((prev) =>
      prev.map((c) =>
        c.id === collectionId ? { ...c, permanent: !c.permanent } : c,
      ),
    );
  };

  const handleToggleThemed = (collectionId: string) => {
    const isTurningOn = !themedCollections.has(collectionId);

    // When turning themed ON, make sure the themed-menu assets exist. If either
    // is missing, open the collection modal so the admin can add them.
    if (isTurningOn) {
      const collection = collections?.find((c) => c.id === collectionId);
      const missingAssets =
        !collection?.splashBackgroundUrl || !collection?.menuLogoUrl;
      if (missingAssets) {
        setAssetModalCollectionId(collectionId);
      }
    }

    setThemedCollections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(collectionId)) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });

    // Update the selected array with the new themed status
    setSelected((prev) =>
      prev.map((c) =>
        c.id === collectionId ? { ...c, themed: !c.themed } : c,
      ),
    );
  };

  const handleSave = () => {
    // Ensure permanent and themed status is applied to all selected collections
    const finalSelection = selected.map((c) => ({
      id: c.id,
      permanent: permanentCollections.has(c.id),
      themed: themedCollections.has(c.id),
    }));
    onPickCollections(finalSelection);
    onClose();
  };

  const handleLoadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <DSDialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallTablet}
    >
      <DSDialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Select Collections
        <DSIconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>
      <DSDialogContent>
        <DSTextField
          type="text"
          placeholder="Search collections..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
          sx={{ mb: "20px", mt: 1 }}
        />

        {isLoading && !collections ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : (
          <DSBox sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {collections && collections.length > 0 ? (
              <>
                {collections.map((collection) => {
                  const isSelected = selected.some(
                    (c) => c.id === collection.id,
                  );
                  const isPermanent = permanentCollections.has(collection.id);
                  const isThemed = themedCollections.has(collection.id);

                  return (
                    <DSBox
                      key={collection.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: "10px",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        cursor: "pointer",
                      }}
                      onClick={() => handleToggleCollection(collection.id)}
                    >
                      <DSCheckBox
                        checked={isSelected}
                        onChange={() => handleToggleCollection(collection.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <DSBox sx={{ ml: "15px", flex: 1 }}>
                        {collection.logoUrl && (
                          <img
                            src={collection.logoUrl}
                            alt={collection.name}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              marginRight: 10,
                              objectFit: "cover",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        <DSTypography
                          sx={{ fontWeight: 500, display: "inline" }}
                        >
                          {collection.name}
                        </DSTypography>
                        {collection.description && (
                          <DSTypography
                            sx={{
                              fontSize: 12,
                              color: "text.secondary",
                              mt: "4px",
                            }}
                          >
                            {collection.description}
                          </DSTypography>
                        )}
                      </DSBox>
                      {isSelected && (
                        <DSBox
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <DSButton
                            variant={isPermanent ? "contained" : "outlined"}
                            color={isPermanent ? "primary" : "secondary"}
                            size="small"
                            startIcon={<FaLock />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePermanent(collection.id);
                            }}
                            title={
                              isPermanent
                                ? "Collection is marked as permanent"
                                : "Mark as permanent"
                            }
                            sx={{ ml: "10px" }}
                          >
                            {isPermanent ? "Permanent" : "Make Permanent"}
                          </DSButton>
                          <DSButton
                            variant={isThemed ? "contained" : "outlined"}
                            color={isThemed ? "primary" : "secondary"}
                            size="small"
                            startIcon={<FaStar />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleThemed(collection.id);
                            }}
                            title={
                              isThemed
                                ? "Collection is marked as themed"
                                : "Mark as themed"
                            }
                            sx={{ ml: "10px" }}
                          >
                            {isThemed ? "Themed" : "Make Themed"}
                          </DSButton>
                        </DSBox>
                      )}
                    </DSBox>
                  );
                })}
                {hasNextPage && (
                  <DSBox sx={{ textAlign: "center", p: "20px" }}>
                    <DSButton variant="text" onClick={handleLoadMore}>
                      Load More
                    </DSButton>
                  </DSBox>
                )}
              </>
            ) : (
              <DSBox sx={{ textAlign: "center", p: "20px" }}>
                No collections found
              </DSBox>
            )}
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Cancel</DSSecondaryButton>
        <DSPrimaryButton onClick={handleSave} disabled={isLoading}>
          Save ({selected.length} selected)
        </DSPrimaryButton>
      </DSDialogActions>

      {assetModalCollectionId && (
        <CollectionDialog
          open
          collectionId={assetModalCollectionId}
          onClose={() => setAssetModalCollectionId(null)}
        />
      )}
    </DSDialog>
  );
}

export default CollectionsPickDialog;
